import { useQueryClient } from "@tanstack/react-query";
import { createContext, use, useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import type { ApiClient } from "../api/client.ts";
import { useAppEnvironment } from "../environment.tsx";
import { Splash } from "../ui/Splash/Splash.tsx";
import type { TelegramLoginData } from "../telegram/telegramLogin.ts";
import {
  createSession,
  type Credentials,
  type ProfileChanges,
  type Registration,
  type Session,
  type SessionState,
} from "./session.ts";

const SessionContext = createContext<Session | null>(null);

/** Восстанавливает сессию при старте (на это время — splash) и очищает кэш, когда она заканчивается. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const environment = useAppEnvironment();
  const queryClient = useQueryClient();
  // Создание сессии без побочных эффектов: StrictMode может вызвать инициализатор дважды.
  const [session] = useState(() => createSession({ baseUrl: environment.apiBaseUrl, fetch: environment.fetch }));
  const { status } = useSyncExternalStore(session.subscribe, session.getState);

  useEffect(() => session.restore(), [session]);

  useEffect(() => {
    if (status === "ended") queryClient.clear();
  }, [status, queryClient]);

  if (status === "restoring") return <Splash />;
  return <SessionContext value={session}>{children}</SessionContext>;
}

function useSessionInstance(): Session {
  const session = use(SessionContext);
  if (!session) throw new Error("SessionProvider is missing");
  return session;
}

export type SessionValue = SessionState & {
  login: (credentials: Credentials) => Promise<void>;
  register: (registration: Registration) => Promise<void>;
  loginWithTelegram: (data: TelegramLoginData) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (changes: ProfileChanges) => Promise<void>;
};

/** Текущий пользователь, вход (по email или через Telegram), регистрация, выход и смена профиля. */
export function useSession(): SessionValue {
  const session = useSessionInstance();
  const state = useSyncExternalStore(session.subscribe, session.getState);
  return {
    ...state,
    login: session.login,
    register: session.register,
    loginWithTelegram: session.loginWithTelegram,
    logout: session.logout,
    updateProfile: session.updateProfile,
  };
}

/** Аутентифицированный API-клиент: сам подставляет access token и обновляет его на 401. */
export function useApiClient(): ApiClient {
  return useSessionInstance().client;
}
